import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useController, useFormContext } from 'react-hook-form';

import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import VideoFile from '@mui/icons-material/VideoFile';
import FilePresent from '@mui/icons-material/FilePresent';
import Avatar from '@mui/material/Avatar';

import { VpIcon } from '../VpIcon';
import VpButton from '../VpButton';

import {
  useFetchClpUploadPresignedUrl,
  useUploadToPresignedUrl
} from '../../../hooks/react-query/useMediaData';
import { useGetSession } from '../../../hooks/useAuthData';
import { useFileUploadData } from '../../../hooks/useFileUploadData';
import { useVpSnackbar } from '../../hooks';

import { StyledDragnDrop, StyledProgressStack } from './styles';
import { getFileSizeLabel } from '../../../utils';

const MAX_FILES = 5;
const MAX_SIZE_MEGABYTES = 500;

export const VpHookFormFileDragAndDrop = ({
  name = 'fileInput',
  limit = MAX_FILES,
  sizeLimit = MAX_SIZE_MEGABYTES,
  setCanSubmitForm,
  setFiles,
  files
}) => {
  const { addSnackbarError } = useVpSnackbar();
  const { mutateAsync: fetchClpUploadPresignedUrl, isLoading: isLoadingPresignedUrl } =
    useFetchClpUploadPresignedUrl();
  const { isLoading: isLoadingAttachment, mutateAsync: uploadAttachment } = useFileUploadData();
  const {
    data: videoUploadData,
    mutate: uploadVideoToPresignedUrl,
    isSuccess: isVideoUploadSuccess,
    isLoading: isLoadingVideoUpload
  } = useUploadToPresignedUrl();
  const { data: authData } = useGetSession();

  const [hovering, setHovering] = useState(false);
  const [currentMediaSubmissionId, setCurrentMediaSubmissionId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [size, setSize] = useState('0 KB');
  const [uploadedFileCount, setUploadedFileCount] = useState(0);

  const isLoading = isLoadingPresignedUrl || isLoadingAttachment || isLoadingVideoUpload;
  setCanSubmitForm(!isLoading);

  const { getValues } = useFormContext();

  const {
    field: { onChange, ref, ...inputProps }
  } = useController({
    name: name,
    rules: { required: true },
    shouldUnregister: true
  });

  const handleFileUpload = (event) => {
    const files = Object.values(event.target.files);
    handleFileReading(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setHovering(true);
  };

  const handleDrop = (event) => {
    setHovering(false);
    event.preventDefault();
    if (isLoading) {
      return addSnackbarError({
        message: 'Please wait for the previous upload to finish before including another file.'
      });
    }
    const files = Object.values(event.dataTransfer.files);
    handleFileReading(files);
  };

  useEffect(() => {
    if (isVideoUploadSuccess) {
      const uploadedFileSize = videoUploadData?.config?.data?.size;
      const oldValue = getValues(name) || [];
      const newUrls = [
        ...oldValue,
        {
          mediaSubmissionId: currentMediaSubmissionId,
          uploadedFileSize
        }
      ];
      setFiles(newUrls);
      onChange(newUrls);
      setCurrentMediaSubmissionId(null);
      setCanSubmitForm(!isLoading);
    }
  }, [isVideoUploadSuccess]);

  const calculateFileSize = (files) => {
    const currentSize = files[0]?.size;

    const fileInputState = getValues(name);

    const sum = fileInputState?.length
      ? fileInputState?.reduce((total, file) => total + (file?.uploadedFileSize || 0), 0) +
        currentSize
      : currentSize;

    const sizeLabel = getFileSizeLabel(sum);
    setSize(sizeLabel);
    return sum;
  };

  const handleFileReading = (files = []) => {
    try {
      if (files.length > limit || getValues(name)?.length + files.length > limit) {
        return addSnackbarError({ message: `You can only upload up to ${limit} files.` });
      }

      const newTotalFileSize = calculateFileSize(files);

      if (newTotalFileSize > sizeLimit * 1000000) {
        return addSnackbarError({ message: `You can only upload up to ${sizeLimit} MB of files.` });
      }

      setUploadedFileCount(uploadedFileCount + 1);
      setUploadProgress(0);

      files?.forEach(async (file, index) => {
        const isVideo = file.type.startsWith('video/');

        if (isVideo) {
          const mediaSubmission = {
            isRecorded: false,
            userAgent: navigator.userAgent,
            screenResolution: `${window.outerWidth}x${window.outerHeight}`, // fix
            userIp: '',
            mimeType: file.type
            // campaignId:
          };

          const presignedUrlData = await fetchClpUploadPresignedUrl(mediaSubmission);
          const mediaSubmissionId = presignedUrlData.mediaSubmissionId;
          setCurrentMediaSubmissionId(mediaSubmissionId);

          uploadVideoToPresignedUrl({
            presignedUrl: presignedUrlData.presignedUrl,
            file
          });
        } else {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('organizationId', authData.data.fileAuthScope.organizationId);
          const uploadAttachmentResponse = await uploadAttachment({ data: formData });
          const attachmentInfo = uploadAttachmentResponse.data;

          const oldContent = getValues('content') || [];
          const newContent = [
            ...oldContent,
            {
              ...attachmentInfo
            }
          ];

          onChange(newContent);
          setFiles(newContent);
        }

        setUploadProgress(((index + 1) / files.length) * 100);
      });
    } catch (e) {
      //remove all previous files
      onChange([]);
      setFiles([]);

      // logging error
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  return (
    <Stack gap="5px" width="100%">
      <Typography variant="inputLabelSmall">Imported Content</Typography>
      <StyledDragnDrop
        onDragOver={handleDragOver}
        onDragLeave={() => setHovering(false)}
        onDrop={handleDrop}
        hovering={hovering}
      >
        <input
          type="file"
          id="Content_Drag_Drop_Input_Field"
          accept="image/*, video/*, application/pdf, text/plain, text/html, application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
        <VpIcon icon="UploadCloud" />
        <Typography variant="greyDark12Bold" align="center">
          Drag and Drop or{' '}
          <VpButton
            variant="text"
            sx={{ padding: 0, margin: 0, fontSize: 12 }}
            disabled={isLoading || hovering} //add file limit check
            component="label"
            htmlFor="Content_Drag_Drop_Input_Field"
          >
            Choose Files
          </VpButton>
          {` to Upload (Max ${limit} files or ${sizeLimit}MB)`}
        </Typography>
        <Typography variant="caption" align="center">
          Videos (MP4 or MOV preferred), Images (PNG or JPEG) and Documents (PDF, csv, xls and docx)
        </Typography>
        <Box sx={{ display: 'flex' }}>
          {files?.map((f, i) => {
            const isVideo = !!f.mediaSubmissionId;
            return (
              <Box
                key={f.uploadedFileSize + i}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <Avatar sx={{ margin: '0 5px', backgroundColor: 'vpColors.blueMain' }}>
                  {isVideo ? <VideoFile /> : <FilePresent />}
                </Avatar>
                <Typography variant="caption">{`${isVideo ? 'Video ' : 'File '} ${
                  i + 1
                }`}</Typography>
              </Box>
            );
          })}
        </Box>
        {isLoading && (
          <Box sx={{ display: 'flex' }}>
            <CircularProgress />
          </Box>
        )}
        <input
          {...inputProps}
          onChange={setFiles}
          ref={ref}
          style={{ display: 'none' }}
          disabled={isLoading}
        />
      </StyledDragnDrop>
      <StyledProgressStack>
        <Stack>
          <VpIcon icon="Video" />
          <Typography variant="darkBold14">{`Upload Progress: ${uploadProgress.toFixed(
            0
          )}%`}</Typography>
        </Stack>
        <Typography variant="caption">{`${size} - ${uploadedFileCount} files uploaded.`}</Typography>
        <LinearProgress variant="determinate" value={uploadProgress} />
      </StyledProgressStack>
    </Stack>
  );
};

VpHookFormFileDragAndDrop.propTypes = {
  defaultValue: PropTypes.string,
  name: PropTypes.string,
  limit: PropTypes.number,
  sizeLimit: PropTypes.number,
  setCanSubmitForm: PropTypes.func,
  setFiles: PropTypes.func,
  files: PropTypes.arrayOf(PropTypes.objkect)
};

export default VpHookFormFileDragAndDrop;



// WEBPACK FOOTER //
// ./src/vp-ui/components/VpHookFormFileDragAndDrop/VpHookFormFileDragAndDrop.jsx